import React, { useMemo } from "react";
import type { EChartsOption } from "echarts";
import EChartsRenderer from "@/components/charts/base/EChartsRenderer";
import type { CallbackDataParams } from "echarts/types/dist/shared";
import { MetricKeys } from "@/constants/metrics";
import { useQuery } from "@tanstack/react-query";
import { fetchTokensChanges, fetchTokensRollups } from "@/services/queries/tokens/tokensScreenerQueries";
import { useMetricsCatalog } from "@/hooks/useMetricsCatalog";
import type { MetricInfo } from "@/types/metrics";

// Stablecoin filtering handled via category map upstream

type Props = {
  rows: Array<Record<string, unknown>>;
  range: '1d' | '7d' | '30d' | '365d';
  xKey: string; // metric to display per token
  xUseChange: boolean;
  logY: boolean;
  setLogYAvailable?: (available: boolean) => void;
  search: string;
  mcMin: number | '';
  mcMax: number | '';
  height?: number | string;
};

const DistributionChart: React.FC<Props> = ({ rows, range, xKey, xUseChange, logY, setLogYAvailable, search, mcMin, mcMax, height = 640 }) => {
  const baseTextStyle = useMemo(() => ({ color: '#e2e8f0' } as const), []);

  // Catalog and helpers
  const { data: catalog } = useMetricsCatalog();
  const catalogMap: Record<string, MetricInfo> = useMemo(() => {
    const map: Record<string, MetricInfo> = {};
    (catalog || []).forEach((m) => { if ((m as MetricInfo)?.metric_key) map[m.metric_key] = m as MetricInfo; });
    return map;
  }, [catalog]);
  const labelFromCatalog = useMemo(() => (key: string): string => {
    const entry = catalogMap[key] as unknown as { display_name?: string | null; label?: string | null; name?: string | null } | undefined;
    return (entry?.display_name || entry?.label || entry?.name || key);
  }, [catalogMap]);

  // Query changes for xKey if using % change
  const changeMetricKeysCsv = useMemo(() => {
    const set = new Set<string>();
    if (xUseChange && !xKey.startsWith('ratio:')) set.add(xKey);
    // Always include price_close to allow optional color mapping later if desired
    set.add('price_close');
    return Array.from(set).join(',');
  }, [xKey, xUseChange]);
  const changesQuery = useQuery({
    queryKey: ["tokens","changes","distribution", changeMetricKeysCsv, range],
    queryFn: ({ signal }) => fetchTokensChanges({ windowsCsv: range, metricKeysCsv: changeMetricKeysCsv, sortBy: "market_cap", sortOrder: "desc", limit: 2000, signal }),
    staleTime: 60_000,
    enabled: changeMetricKeysCsv.length > 0,
  });
  const changeByTickerMetric = useMemo(() => {
    const out = new Map<string, Map<string, number>>();
    const tokens = changesQuery.data?.tokens || [];
    for (const t of tokens) {
      const inner = new Map<string, number>();
      for (const m of (t.metrics || [])) {
        const raw = m?.windows?.[range]?.value;
        const pct = typeof raw === 'number' ? (raw * 100) : 0;
        inner.set(m.metric_key, pct);
      }
      // Store by uppercase ticker for consistent lookup
      out.set(t.ticker.toUpperCase(), inner);
    }
    return out;
  }, [changesQuery.data, range]);

  // Query rollups for snapshot metrics
  const rollupsQuery = useQuery({
    queryKey: ["tokens","rollups","distribution", range, xKey],
    queryFn: ({ signal }) => (
      xUseChange
        ? fetchTokensRollups({ windowsCsv: range, sortBy: "market_cap", sortOrder: "desc", limit: 2000, signal })
        : fetchTokensRollups({ windowsCsv: range, metricKeysCsv: xKey, sortBy: "market_cap", sortOrder: "desc", limit: 2000, signal })
    ),
    staleTime: 60_000,
    enabled: !xUseChange,
  });
  const rollByTicker = useMemo(() => {
    const out = new Map<string, Record<string, number | null>>();
    const toks = rollupsQuery.data?.tokens || [];
    for (const t of toks) {
      const rec: Record<string, number | null> = {};
      for (const m of t.metrics || []) {
        const v = (m.windows?.[range]?.value ?? null) as number | null;
        rec[m.metric_key] = v;
      }
      // Store by uppercase ticker for consistent lookup
      out.set(t.ticker.toUpperCase(), rec);
    }
    return out;
  }, [rollupsQuery.data, range]);

  const getChangePct = React.useCallback((ticker: string, metricKey: string): number => {
    const inner = changeByTickerMetric.get(ticker);
    if (!inner) return 0;
    return inner.get(metricKey) ?? 0;
  }, [changeByTickerMetric]);

  const getMetricValue = React.useCallback((ticker: string, key: string, row: Record<string, unknown>, useChange: boolean): number | null => {
    if (useChange && !key.startsWith('ratio:')) {
      return getChangePct(ticker, key);
    }
    const roll = rollByTicker.get(ticker);
    if (roll && Object.prototype.hasOwnProperty.call(roll, key)) {
      const v = roll[key];
      if (v == null || v === undefined) return null;
      const num = Number(v);
      return Number.isFinite(num) ? num : null;
    }
    const snap = row[key];
    if (snap == null || snap === undefined) return null;
    const num = Number(snap);
    return Number.isFinite(num) ? num : null;
  }, [getChangePct, rollByTicker]);

  const { option, count } = useMemo(() => {
    type Point = { name: string; value: number };
    const points: Point[] = [];
    for (const r of rows as Array<Record<string, unknown>>) {
      const tickerRaw = String(r["tokenSymbol"] ?? "");
      if (!tickerRaw) continue;
      const ticker = tickerRaw.toUpperCase(); // Normalize to uppercase for consistent lookup
      // excludeStables handled upstream
      if (search) {
        const name = String(r["tokenName"] ?? r["name"] ?? "");
        const s = search.toLowerCase();
        if (!ticker.toLowerCase().includes(s) && !name.toLowerCase().includes(s)) continue;
      }
      const mcRaw = (r["market_cap"] as number | null) ?? (r[MetricKeys.MarketCap] as number | null) ?? null;
      const mc = mcRaw == null ? NaN : Number(mcRaw);
      if (!Number.isFinite(mc) || mc <= 0) continue;
      if (mcMin !== '' && mc < Number(mcMin)) continue;
      if (mcMax !== '' && mc > Number(mcMax)) continue;
      const xv = getMetricValue(ticker, xKey, r, xUseChange);
      if (xv == null || !Number.isFinite(xv)) continue;
      if (!xUseChange && Number(xv) === 0) continue;
      points.push({ name: ticker, value: Number(xv) });
    }

    // Sort by value desc
    points.sort((a, b) => b.value - a.value);
    const labels = points.map((p) => p.name);
    const values = points.map((p) => p.value);
    const hasData = points.length > 0;

    // Axis setup
    const hasNegative = values.some((v) => Number.isFinite(v) && v < 0);
    const hasZeroOrNeg = values.some((v) => !Number.isFinite(v) || v <= 0);
    if (typeof setLogYAvailable === 'function') setLogYAvailable(!hasNegative);
    const canUseLog = !hasNegative && values.length > 0;
    const yAxisType = (logY && canUseLog) ? 'log' : 'value';

    // Tooltip formatter based on unit or percent
    const unit = catalogMap[String(xKey)]?.unit as string | undefined;
    const formatVal = (val: number) => {
      if (xUseChange) {
        const a = Math.abs(val);
        if (a < 0.01) return `${val.toFixed(4)}%`;
        if (a < 0.1) return `${val.toFixed(3)}%`;
        return `${val.toFixed(2)}%`;
      }
      if (unit && (unit.toLowerCase().includes('usd') || unit.includes('$') || unit.toLowerCase() === 'currency')) {
        if (Math.abs(val) >= 1e9) return `$${(val/1e9).toFixed(2)}B`;
        if (Math.abs(val) >= 1e6) return `$${(val/1e6).toFixed(2)}M`;
        if (Math.abs(val) >= 1e3) return `$${(val/1e3).toFixed(2)}K`;
        return `$${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
      }
      return Math.abs(val) >= 1 ? val.toLocaleString(undefined, { maximumFractionDigits: 2 }) : val.toFixed(4);
    };

    const option: EChartsOption = {
      backgroundColor: 'transparent',
      textStyle: baseTextStyle,
      grid: { left: 60, right: 16, top: 20, bottom: 60 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params: unknown) => {
          const items = params as Array<{ name: string; value: number }>;
          const it = items?.[0];
          if (!it) return '';
          return `${it.name}<br/>${labelFromCatalog(String(xKey))}${xUseChange ? ' (%)' : ''}: ${formatVal(Number(it.value || 0))}`;
        },
      },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { color: '#94a3b8', rotate: 45, align: 'right' as const },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: ((): unknown => {
        if (yAxisType === 'log') {
          const base = {
            type: 'log' as const,
            axisLabel: { color: '#94a3b8', formatter: (v: number) => formatVal(Number(v)) },
            splitLine: { show: true, lineStyle: { color: 'rgba(148,163,184,0.2)' } },
          };
          return hasZeroOrNeg ? { ...base, min: 1e-8 } : base;
        }
        return {
          type: 'value' as const,
          scale: true, // Auto-fit axis range to data
          axisLabel: { color: '#94a3b8', formatter: (v: number) => formatVal(Number(v)) },
          splitLine: { show: true, lineStyle: { color: 'rgba(148,163,184,0.2)' } },
        };
      })() as NonNullable<EChartsOption['yAxis']>,
      series: hasData ? [
        {
          type: 'bar',
          data: (logY && canUseLog)
            ? values.map((v) => (v <= 0 ? 1e-8 : v))
            : values,
          barWidth: '65%',
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: xUseChange ? ((p: CallbackDataParams) => {
              const vv = Array.isArray(p.value) ? Number(p.value[1]) : Number(p.value as number);
              return vv < 0 ? '#ef4444' : '#22c55e';
            }) : '#60a5fa',
            opacity: 0.9,
          },
          emphasis: { focus: 'series' },
        }
      ] : [],
    };

    return { option, count: points.length };
  }, [rows, search, mcMin, mcMax, xKey, xUseChange, logY,  getMetricValue, baseTextStyle, labelFromCatalog, catalogMap, setLogYAvailable]);

  const isLoading = changesQuery.isLoading || rollupsQuery.isLoading;
  const error = (changesQuery.error as Error | null | undefined)?.message || (rollupsQuery.error as Error | null | undefined)?.message || null;
  return <EChartsRenderer chartOption={option} height={height} isLoading={isLoading} error={error} hasData={count > 0} containerClassName="bg-transparent border-0 rounded-lg overflow-hidden" />;
};

export default DistributionChart;


